import { useId } from "react";

import { OwnerSaleDialog } from "./OwnerSaleDialog.tsx";
import { OWNER_SALE_COPY } from "./ownerSale.constants.ts";
import { useOwnerSaleController } from "./useOwnerSaleController.ts";
import "./ownerSale.css";

/**
 * The owner's own sale, on the work's own record — OND-173.
 *
 * MOUNTED BESIDE `ConsignPanel`, and the order is the argument: the panel above
 * says who may sell this work on the owner's behalf, and this one is the owner
 * doing it themselves. The two are mutually exclusive by the engine's own rule
 * — a work under a live accepted consignment is the gallery's to sell — so a
 * reader never has two live ways to move the same picture.
 *
 * IT HANGS OFF THE WORK BECAUSE THE ACT DOES. `POST /v1/works/:id/sale` is
 * addressed to the work rather than to a consignment, for the reason
 * `OwnerSaleController`'s header gives: there is no consignment to address, and
 * the authority is the owner-level grant on the collection holding the piece.
 * The gallery's equivalent is a dialog opened from its inventory, because there
 * the consignment IS the subject.
 *
 * `canSell` DRAWS THE CONTROL AND DECIDES NOTHING ELSE. It is
 * `capabilities.transferWork` — the server's answer about whether this account
 * holds anything sellable at all, the same hint `canConsign` is — and it is
 * passed rather than read here, so one answer is on the screen. Never a role
 * string, and never `if (featureNotBuilt)`: OND-173's own description names a
 * capability that is true in front of somebody with nothing to press as "the
 * same failure as a feature flag, reached from the other side", and this
 * component is the door that keeps it from being either.
 *
 * The view holds no business condition: which of three states the panel is in,
 * which step may be left and what the press will do are all decided in
 * `ownerSale.model.ts` and `useOwnerSaleController.ts`.
 */
export function OwnerSalePanel({
  workId,
  subject,
  canSell,
  onSold,
}: {
  workId: string;
  /** How the read-back names the work — '"Marsh Light" · Agnes Thorne · ONDO-W-…'. */
  subject: string;
  /** `capabilities.transferWork`, from the workspace. */
  canSell: boolean;
  /** Re-read the record. A sale writes a transfer and a line of provenance. */
  onSold: () => void;
}) {
  const controller = useOwnerSaleController(workId, subject, canSell, onSold);
  const { panel } = controller;
  const headingId = useId();

  // LITERALLY NOTHING, not a disabled button and not an explanatory gap. A
  // reader with no capability is not the owner of anything sellable, and a
  // control they cannot use is a control that invites a refusal.
  if (panel.kind === "hidden") return null;

  return (
    // NAMED BY ITS OWN HEADING, so the `<section>` is a landmark a screen
    // reader can reach and announce rather than an anonymous box. The record's
    // aside is a column of panels about one work, and a reader arriving at this
    // one has to hear which act it is before the control in it.
    <section className="ownsale" aria-labelledby={headingId}>
      <header className="ownsale__head">
        <h2 id={headingId} className="ownsale__title">
          {OWNER_SALE_COPY.panel.heading}
        </h2>
        {panel.kind === "offer" && (
          <button type="button" className="ownsale__action" onClick={controller.open}>
            {OWNER_SALE_COPY.panel.open}
          </button>
        )}
      </header>

      {panel.kind === "consigned" ? (
        <p className="ownsale__note">{OWNER_SALE_COPY.panel.consigned}</p>
      ) : (
        <>
          <p className="ownsale__lead">{OWNER_SALE_COPY.panel.lead}</p>
          {panel.recorded && (
            <p className="ownsale__recorded" role="status">
              {OWNER_SALE_COPY.panel.recorded}
            </p>
          )}
        </>
      )}

      {/* Mounted even when shut — closing by unmounting would skip close(), and
          it is close() that returns focus to the button that opened it. Not
          drawn at all on a consigned work: there is nothing to open. */}
      {panel.kind === "offer" && <OwnerSaleDialog controller={controller} />}
    </section>
  );
}
